import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    InputBase,
    Box,
    Avatar,
    Menu,
    MenuItem,
    Badge,
    useTheme,
    alpha,
    Tooltip,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Divider,
    Paper,
    CircularProgress
} from '@mui/material';
import {
    Search as SearchIcon,
    Brightness4 as DarkIcon,
    Brightness7 as LightIcon,
    Notifications as NotificationsIcon,
    KeyboardArrowDown,
    Menu as MenuIcon,
    TrendingUp as TrendingIcon,
    Login as LoginIcon,
    CheckCircle as CheckIcon,
    Message as MessageIcon,
    Visibility as ViewIcon,
    Article as ArticleIcon,
    ThumbUp as ThumbUpIcon,
    Business as BusinessIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getMyNotifications, getUnreadCount, markAsRead, markAllAsRead, Notification } from '../../services/notification';
import { formatDistanceToNow } from 'date-fns';

// Logo imports should be dynamic based on theme
import logoDark from '../../assets/logo-white.png';
import logoLight from '../../assets/logo-black.png';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    background: theme.palette.mode === 'dark'
        ? `linear-gradient(90deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha('#002b34', 0.8)} 100%)`
        : `linear-gradient(90deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha('#86dffd', 0.1)} 100%)`,
    backdropFilter: 'blur(10px)',
    boxShadow: theme.palette.mode === 'dark'
        ? '0 4px 20px rgba(0,0,0,0.3)'
        : '0 4px 20px rgba(0,0,0,0.05)',
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    transition: 'all 0.3s ease'
}));

const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: 30,
    backgroundColor: alpha(theme.palette.common.white, 0.07),
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.12),
        boxShadow: `0 0 0 2px ${alpha('#00b2fc', 0.1)}`
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(3),
        width: 'auto',
    },
    transition: 'all 0.2s ease'
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: alpha(theme.palette.text.primary, 0.6)
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: theme.palette.text.primary,
    '& .MuiInputBase-input': {
        padding: theme.spacing(1.2, 1, 1.2, 0),
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: '24ch',
            '&:focus': {
                width: '32ch',
            },
        },
    },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    height: 70,
    [theme.breakpoints.up('sm')]: {
        paddingLeft: theme.spacing(3),
    },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
}));

const Logo = styled('img')(({ theme }) => ({
    height: 42,
    transition: 'transform 0.3s ease',
    '&:hover': {
        transform: 'scale(1.05)'
    }
}));

const RightSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
}));

const UserSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(2),
    borderLeft: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    paddingLeft: theme.spacing(2),
    cursor: 'pointer',
    '&:hover': {
        '.MuiAvatar-root': {
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}`
        }
    }
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    borderRadius: 14,
    padding: theme.spacing(1),
    marginLeft: theme.spacing(1),
    backgroundColor: alpha('#00b2fc', 0.05),
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: alpha('#00b2fc', 0.15),
        transform: 'translateY(-2px)'
    }
}));

const AnimatedBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        backgroundColor: theme.palette.error.main,
        transition: 'all 0.2s ease',
        '&::after': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            animation: 'ripple 1.2s infinite ease-in-out',
            border: '1px solid currentColor',
            content: '""',
        },
    },
    '@keyframes ripple': {
        '0%': {
            transform: 'scale(.8)',
            opacity: 1,
        },
        '100%': {
            transform: 'scale(2.4)',
            opacity: 0,
        },
    },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
    width: 40,
    height: 40,
    backgroundColor: '#00b2fc',
    border: `2px solid ${alpha(theme.palette.background.paper, 0.8)}`,
    transition: 'all 0.3s ease',
    boxShadow: `0 3px 10px ${alpha(theme.palette.common.black, 0.1)}`
}));

const StatusIndicator = styled('div')<{ online: boolean }>(({ theme, online }) => ({
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: online ? '#d9f316' : theme.palette.grey[500],
    border: `2px solid ${theme.palette.background.paper}`,
    position: 'absolute',
    bottom: 0,
    right: 0
}));

interface HeaderProps {
    toggleTheme: () => void;
    toggleSidebar: () => void;
    currentTheme: 'light' | 'dark';
    isAuthenticated: boolean;
    user: any;
    onLogout: () => void;
}

const Header = ({ toggleTheme, toggleSidebar, currentTheme, isAuthenticated, user, onLogout }: HeaderProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loadingNotifications, setLoadingNotifications] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const isDarkMode = currentTheme === 'dark';

    const logo = isDarkMode ? logoDark : logoLight;

    // Fetch unread count periodically
    useEffect(() => {
        if (isAuthenticated) {
            fetchUnreadCount();
            const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
            return () => clearInterval(interval);
        }
    }, [isAuthenticated]);

    const fetchUnreadCount = async () => {
        try {
            const count = await getUnreadCount();
            setUnreadCount(count);
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    };

    const fetchNotifications = async () => {
        setLoadingNotifications(true);
        try {
            const notifs = await getMyNotifications();
            setNotifications(notifs);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoadingNotifications(false);
        }
    };

    const handleNotificationOpen = async (event: React.MouseEvent<HTMLElement>) => {
        setNotificationAnchor(event.currentTarget);
        await fetchNotifications();
    };

    const handleNotificationClose = () => {
        setNotificationAnchor(null);
    };

    const handleNotificationClick = async (notification: Notification) => {
        try {
            if (!notification.isRead) {
                await markAsRead(notification.id);
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
            if (notification.link) {
                navigate(notification.link);
            }
            handleNotificationClose();
        } catch (error) {
            console.error('Error handling notification click:', error);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await markAllAsRead();
            setUnreadCount(0);
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleNavigate = (path: string) => {
        navigate(path);
        handleMenuClose();
    };

    const handleLogin = () => {
        navigate('/login');
    };

    const handleSearch = (event: React.FormEvent) => {
        event.preventDefault();
        if (searchTerm.trim()) {
            // Navigate to search results page
            navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
            setSearchTerm('');
        }
    };

    const getUserInitials = () => {
        if (user && user.firstName && user.lastName) {
            return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
        }
        return 'GP';
    };

    const getUserType = () => {
        if (!user) return 'Member';
        if (user.userType === 'admin') return 'Admin';
        if (user.userType === 'expert') return 'Expert';
        return 'Member';
    };

    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'message':
                return <MessageIcon fontSize="small" />;
            case 'view_count':
                return <ViewIcon fontSize="small" />;
            case 'article_published':
            case 'article_updated':
                return <ArticleIcon fontSize="small" />;
            case 'testimonial_approved':
            case 'testimonial_rejected':
                return <ThumbUpIcon fontSize="small" />;
            case 'project_approved':
            case 'project_rejected':
            case 'investor_request_approved':
            case 'investor_request_rejected':
                return <BusinessIcon fontSize="small" />;
            case 'contact_request':
            case 'connection_accepted':
            case 'mentorship_request':
            case 'mentorship_accepted':
                return <PersonIcon fontSize="small" />;
            default:
                return <NotificationsIcon fontSize="small" />;
        }
    };

    return (
        <StyledAppBar position="fixed">
            <StyledToolbar>
                <Box display="flex" alignItems="center">
                    {isAuthenticated && (
                        <IconButton
                            color="inherit"
                            aria-label="toggle sidebar"
                            edge="start"
                            onClick={toggleSidebar}
                            sx={{
                                borderRadius: 2,
                                mr: 1,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    transform: 'rotate(5deg)'
                                }
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}

                    <LogoContainer>
                        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                            <Logo src={logo} alt="Value Chain Global" />
                        </Link>
                    </LogoContainer>

                    {isAuthenticated && (
                        <Box ml={3} mr={1} display={{ xs: 'none', lg: 'flex' }} alignItems="center">
                            <Button
                                sx={{
                                    borderRadius: 4,
                                    px: 2,
                                    mx: 0.5,
                                    textTransform: 'none',
                                    fontFamily: '"Poppins", sans-serif',
                                    fontWeight: 500,
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        backgroundColor: alpha('#00b2fc', 0.1)
                                    }
                                }}
                                startIcon={<TrendingIcon fontSize="small" />}
                                onClick={() => handleNavigate('/industries')}
                            >
                                Explore All Industries
                            </Button>
                        </Box>
                    )}
                </Box>

                <RightSection>
                    {isAuthenticated && (
                        <Search as="form" onSubmit={handleSearch}>
                            <SearchIconWrapper>
                                <SearchIcon />
                            </SearchIconWrapper>
                            <StyledInputBase
                                placeholder="Search industries, opportunities, offers..."
                                inputProps={{ 'aria-label': 'search' }}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </Search>
                    )}

                    <Tooltip title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}>
                        <StyledIconButton onClick={toggleTheme} color="inherit">
                            {isDarkMode ? <LightIcon /> : <DarkIcon />}
                        </StyledIconButton>
                    </Tooltip>

                    {isAuthenticated ? (
                        <>
                            <Tooltip title="Notifications">
                                <StyledIconButton onClick={handleNotificationOpen}>
                                    <AnimatedBadge badgeContent={unreadCount} color="error">
                                        <NotificationsIcon />
                                    </AnimatedBadge>
                                </StyledIconButton>
                            </Tooltip>

                            <UserSection onClick={handleProfileMenuOpen}>
                                <Box position="relative" mr={2}>
                                    <UserAvatar src={user?.photoUrl || undefined}>
                                        {!user?.photoUrl && getUserInitials()}
                                    </UserAvatar>
                                    <StatusIndicator online={true} />
                                </Box>
                                <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        fontFamily='"Poppins", sans-serif'
                                    >
                                        {user ? `${user.firstName} ${user.lastName}` : 'User'}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        fontFamily='"Poppins", sans-serif'
                                    >
                                        {getUserType()}
                                    </Typography>
                                </Box>
                                <KeyboardArrowDown fontSize="small" sx={{ ml: 1, opacity: 0.7 }} />
                            </UserSection>
                        </>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<LoginIcon />}
                            onClick={handleLogin}
                            sx={{
                                borderRadius: 2,
                                ml: 2,
                                textTransform: 'none',
                                fontWeight: 500
                            }}
                        >
                            Log In
                        </Button>
                    )}
                </RightSection>
            </StyledToolbar>

            {/* Profile Menu */}
            {isAuthenticated && (
                <Menu
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    PaperProps={{
                        sx: {
                            mt: 1.5,
                            boxShadow: theme.palette.mode === 'dark'
                                ? '0 8px 20px rgba(0,0,0,0.4)'
                                : '0 8px 20px rgba(0,0,0,0.1)',
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                        }
                    }}
                >
                    <MenuItem onClick={() => handleNavigate('/profile')}>My Profile</MenuItem>
                    <MenuItem onClick={() => handleNavigate('/my-industries')}>My Industries</MenuItem>
                    <MenuItem onClick={() => handleNavigate('/settings')}>Settings</MenuItem>
                    <MenuItem onClick={onLogout}>Sign Out</MenuItem>
                </Menu>
            )}

            {/* Notifications Menu */}
            {isAuthenticated && (
                <Menu
                    anchorEl={notificationAnchor}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    open={Boolean(notificationAnchor)}
                    onClose={handleNotificationClose}
                    PaperProps={{
                        sx: {
                            mt: 1.5,
                            width: 420,
                            maxWidth: '100%',
                            maxHeight: 500,
                            boxShadow: theme.palette.mode === 'dark'
                                ? '0 8px 20px rgba(0,0,0,0.4)'
                                : '0 8px 20px rgba(0,0,0,0.1)',
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                        }
                    }}
                >
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight={600}>
                            Notifications
                        </Typography>
                        {unreadCount > 0 && (
                            <Button
                                size="small"
                                onClick={handleMarkAllRead}
                                sx={{ textTransform: 'none' }}
                            >
                                Mark all as read
                            </Button>
                        )}
                    </Box>
                    <Divider />
                    {loadingNotifications ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : notifications.length > 0 ? (
                        <List sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
                            {notifications.map((notification, index) => (
                                <Box key={notification.id}>
                                    <ListItem
                                        component="button"
                                        onClick={() => handleNotificationClick(notification)}
                                        sx={{
                                            backgroundColor: notification.isRead
                                                ? 'transparent'
                                                : alpha(theme.palette.primary.main, 0.08),
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.primary.main, 0.12)
                                            },
                                            cursor: 'pointer',
                                            border: 'none',
                                            width: '100%',
                                            textAlign: 'left'
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15) }}>
                                                {getNotificationIcon(notification.type)}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {notification.title}
                                                    </Typography>
                                                    {!notification.isRead && (
                                                        <Box
                                                            sx={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: '50%',
                                                                bgcolor: 'primary.main'
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {notification.message}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                                                    </Typography>
                                                </>
                                            }
                                        />
                                    </ListItem>
                                    {index < notifications.length - 1 && <Divider />}
                                </Box>
                            ))}
                        </List>
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                            <Typography color="text.secondary">
                                No notifications yet
                            </Typography>
                        </Box>
                    )}
                </Menu>
            )}
        </StyledAppBar>
    );
};

export default Header;
